import type { AxiosResponse } from 'axios';

import { ADMIN, baseRoutes, baseRoutesV2, baseUrl } from '@/api/config';
import type { VideoAdvType } from '@/api/routes/static-adv/types';
import type { ResponseWithPagination } from '@/utils/types';

import axiosInstance from '../../instance';
import ROUTES from '../../routes';
import type {
  AdsBody,
  CampaignBody,
  CampaignFilters,
  CampaignNames,
  CampaignRequestFilters, CampaignStatResponse, CampaignText, ChangeTagsAdBody,
  ForceModerationAdBody,
  ICampaign,
  RetryModerationAdBody,
  ToggleArchivedCampaignBody,
  ToggleEnabledAdBody,
  ToggleOrgEnabledAdBody,
  ToggleModerationAdBodyNew,
  ToggleModerationAdWidgetBody,
} from './types';

export const getAll = (data: Partial<CampaignBody>)
  : Promise<void | AxiosResponse<ResponseWithPagination<ICampaign>>> => axiosInstance({
  url: `${ROUTES.CAMPAIGNS}/select`,
  method: 'POST',
  headers: {
    authorization: true,
  },
  data,
});

export const getAds = (data: Partial<AdsBody>)
  : Promise<void | AxiosResponse<ResponseWithPagination<ICampaign>>> => axiosInstance({
  url: `${ROUTES.ADS}/select`,
  method: 'POST',
  headers: {
    authorization: true,
  },
  data,
});

export const getById = (id: string)
  : Promise<void | AxiosResponse<ICampaign>> => axiosInstance({
  url: `${ROUTES.CAMPAIGNS}/${id}`,
  method: 'GET',
  headers: {
    authorization: true,
  },
});

export const getFilters = (data: CampaignRequestFilters)
    : Promise<void | AxiosResponse<CampaignFilters>> => axiosInstance({
  baseURL: `${baseUrl}${baseRoutesV2}${ADMIN}`,
  url: `${ROUTES.CAMPAIGNS}/select/filters`,
  method: 'POST',
  headers: {
    authorization: true,
  },
  data,
});

export const getNames = (data: CampaignText<VideoAdvType>)
    : Promise<void | AxiosResponse<CampaignNames>> => axiosInstance({
  url: `${ROUTES.CAMPAIGNS}/select/names`,
  method: 'POST',
  headers: {
    authorization: true,
  },
  data,
});

export const update = (id: string, data: ICampaign)
  : Promise<void | AxiosResponse<{ result: 0 }>> => axiosInstance({
  url: `${ROUTES.CAMPAIGNS}/${id}`,
  method: 'POST',
  headers: {
    authorization: true,
  },
  data,
});

export const toggleEnabledCampaign = (id: string, data: Pick<ICampaign, 'enabled'>)
  : Promise<void | AxiosResponse<{ result: 0 }>> => axiosInstance({
  url: `${ROUTES.CAMPAIGNS}/${id}`,
  method: 'PATCH',
  headers: {
    authorization: true,
  },
  data,
});

export const toggleOrgEnabledCampaign = (id: string, data: Pick<ICampaign, 'org_enabled'>)
  : Promise<void | AxiosResponse<{ result: 0 }>> => axiosInstance({
  url: `${ROUTES.CAMPAIGNS}/${id}`,
  method: 'PATCH',
  headers: {
    authorization: true,
  },
  data,
});

export const toggleEnabledAd = (campaignId: string, data: ToggleEnabledAdBody)
  : Promise<void | AxiosResponse<{ result: 0 }>> => axiosInstance({
  url: `${ROUTES.CAMPAIGNS}/${campaignId}`,
  method: 'PATCH',
  headers: {
    authorization: true,
  },
  data,
});

export const toggleOrgEnabledAd = (campaignId: string, data: ToggleOrgEnabledAdBody)
  : Promise<void | AxiosResponse<{ result: 0 }>> => axiosInstance({
  url: `${ROUTES.CAMPAIGNS}/${campaignId}`,
  method: 'PATCH',
  headers: {
    authorization: true,
  },
  data,
});

export const toggleAdminModerationAdNew = (adsId: string, data: ToggleModerationAdBodyNew)
  : Promise<void | AxiosResponse<{ result: 0 }>> => axiosInstance({
  url: `${ROUTES.ADS}/${adsId}/admin-mod/submit`,
  method: 'POST',
  headers: {
    authorization: true,
  },
  data,
});

export const toggleAdminModerationAdWidget = (adsId: string, data: ToggleModerationAdWidgetBody)
  : Promise<void | AxiosResponse<{ result: 0 }>> => axiosInstance({
  url: `${ROUTES.TABLET_WIDGETS}/${adsId}/admin-mod`,
  method: 'POST',
  headers: {
    authorization: true,
  },
  data,
});

export const retryModerationAd = (campaignId: string, data: RetryModerationAdBody)
  : Promise<void | AxiosResponse<{ result: 0 }>> => axiosInstance({
  url: `${ROUTES.CAMPAIGNS}/${campaignId}`,
  method: 'PATCH',
  headers: {
    authorization: true,
  },
  data,
});

export const forceModerationAd = (campaignId: string, data: ForceModerationAdBody)
  : Promise<void | AxiosResponse<{ result: 0 }>> => axiosInstance({
  url: `${ROUTES.CAMPAIGNS}/${campaignId}`,
  method: 'PATCH',
  headers: {
    authorization: true,
  },
  data,
});

export const toggleArchivedCampaign = (campaignId: string, data: ToggleArchivedCampaignBody)
  : Promise<void | AxiosResponse<{ result: 0 }>> => axiosInstance({
  url: `${ROUTES.CAMPAIGNS}/${campaignId}`,
  method: 'PATCH',
  headers: {
    authorization: true,
  },
  data,
});

export const changeTagsCampaign = (campaignId: string, data: ChangeTagsAdBody)
    : Promise<void | AxiosResponse<{ result: 0 }>> => axiosInstance({
  url: `${ROUTES.CAMPAIGNS}/${campaignId}`,
  method: 'PATCH',
  headers: {
    authorization: true,
  },
  data,
});

export const getStat = (): Promise<void | AxiosResponse<CampaignStatResponse>> => axiosInstance({
  baseURL: `${baseUrl}${baseRoutesV2}${ADMIN}`,
  url: `${ROUTES.CAMPAIGNS}/stat`,
  method: 'GET',
  headers: {
    authorization: true,
  },
});

export const generateQR = (data: {
  obj_type: 'campaign';
  obj_id: string;
  url: string;
  key: string;
}): Promise<void | AxiosResponse<File>> =>
  axiosInstance({
    url: 'short-url/qr-preview',
    method: 'POST',
    headers: {
      authorization: true,
    },
    responseType: 'blob',
    baseURL: `${baseRoutes}/`,
    data,
  });